import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Phone, Mail, CheckCircle, MapPin, Clock, CalendarDays } from 'lucide-react';
import { SiteNav } from '../components/SiteNav';
import { SiteFooter } from '../components/SiteFooter';
import { getSeason, SEASONS } from '../lib/season';
import { services } from '../mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Service area: a booking is allowed only if the selected address is within the radius
// of one of these centers (mirrors the backend). DFW metroplex = 30 mi around its center
// (DFW Airport); Waxahachie home base = 15 mi.
const SERVICE_AREA_CENTERS = [
  { lat: 32.8998, lng: -97.0403, radius: 30 }, // DFW Metroplex
  { lat: 32.3865, lng: -96.8484, radius: 15 }, // Waxahachie
];
const milesBetween = (lat1, lng1, lat2, lng2) => {
  const R = 3958.7613;
  const rad = (x) => (x * Math.PI) / 180;
  const dphi = rad(lat2 - lat1);
  const dlmb = rad(lng2 - lng1);
  const a = Math.sin(dphi / 2) ** 2 +
    Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dlmb / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};
const isInServiceArea = (lat, lng) =>
  SERVICE_AREA_CENTERS.some((c) => milesBetween(lat, lng, c.lat, c.lng) <= c.radius);

const pad = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const tomorrowDate = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(0, 0, 0, 0); return d; };
const todayDate = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const maxDate = () => { const d = new Date(); d.setDate(d.getDate() + 30); d.setHours(0, 0, 0, 0); return d; };
const monthRange = (monthDate) => {
  const start = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const end = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  return { start: toISO(start), end: toISO(end) };
};
const prettyDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};

export const OnePage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    notes: '',
    smsConsent: false
  });

  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null); // { time, label, arrival_window }
  const [sameDayInfo, setSameDayInfo] = useState({ same_day: false, premium: 100 });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(tomorrowDate());
  const [monthDays, setMonthDays] = useState({}); // { 'YYYY-MM-DD': { status, available } }

  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [mapsReady, setMapsReady] = useState(
    () => !!(window.google && window.google.maps && window.google.maps.places)
  );
  const [coords, setCoords] = useState(null); // { lat, lng } from selected address

  // Service zone derived from the address (matches backend logic)
  const zone = formData.address.toLowerCase().includes('waxahachie') ? 'waxahachie' : 'outside';
  const inServiceArea = coords ? isInServiceArea(coords.lat, coords.lng) : false;

  const resetBookingSelection = () => {
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
  };

  // Poll the backend for same-day payment status after returning from Stripe Checkout
  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 6) {
      toast.error('Still processing', { id: 'pay', description: 'Payment is taking longer than expected. Check your email for confirmation or call us at 469-505-4321.' });
      return;
    }
    try {
      const res = await axios.get(`${API}/api/payments/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Same-day appointment confirmed!', { id: 'pay', description: 'Payment received — check your email/phone for details.' });
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Payment session expired', { id: 'pay', description: 'Please try booking again.' });
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      toast.error('Could not verify payment', { id: 'pay', description: 'Please call or text us at 469-505-4321.' });
    }
  };

  // Handle return from Stripe Checkout (same-day premium)
  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(window.location.search);
    const booking = params.get('booking');
    const sessionId = params.get('session_id');
    if (booking === 'cancelled') {
      toast.error('Payment cancelled', { description: 'Your same-day appointment was not booked. You can try again or call us.' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (booking === 'success' && sessionId) {
      toast.loading('Confirming your payment…', { id: 'pay' });
      pollPaymentStatus(sessionId, 0);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Changing the address can change the service zone & schedule -> reset any picked date/slot.
    // Manual typing invalidates the previously selected coordinates (must re-pick a suggestion).
    if (name === 'address') {
      setCoords(null);
      resetBookingSelection();
    }
  };

  // Load Google Maps Places script once
  useEffect(() => {
    if (mapsReady) return;
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) return;
    const existing = document.getElementById('gmaps-places-script');
    if (existing) { existing.addEventListener('load', () => setMapsReady(true)); return; }
    const s = document.createElement('script');
    s.id = 'gmaps-places-script';
    s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    s.async = true;
    s.defer = true;
    s.onload = () => setMapsReady(true);
    document.body.appendChild(s);
  }, [mapsReady]);

  // Attach Places Autocomplete to the address input
  useEffect(() => {
    if (!mapsReady || !addressInputRef.current || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(addressInputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['formatted_address', 'address_components', 'geometry'],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      const formatted = place.formatted_address || addressInputRef.current.value;
      const loc = place.geometry && place.geometry.location;
      setCoords(loc ? { lat: loc.lat(), lng: loc.lng() } : null);
      setFormData((prev) => ({ ...prev, address: formatted }));
      resetBookingSelection();
    });
    autocompleteRef.current = ac;
  }, [mapsReady]);

  useEffect(() => {
    if (!inServiceArea) return;
    let active = true;
    (async () => {
      const { start, end } = monthRange(calendarMonth);
      try {
        const res = await axios.get(`${API}/api/availability/calendar`, { params: { start, end, zone } });
        if (active) setMonthDays((prev) => ({ ...prev, ...res.data.days }));
      } catch (err) {
        // leave existing data; days will appear disabled
      }
    })();
    return () => { active = false; };
  }, [calendarMonth, zone, inServiceArea]);

  const fetchSlots = async (date) => {
    setSelectedSlot(null);
    setSlots([]);
    if (!date) return;
    setLoadingSlots(true);
    try {
      const res = await axios.get(`${API}/api/availability`, { params: { date, zone } });
      setSlots(res.data.slots || []);
      setSameDayInfo({ same_day: !!res.data.same_day, premium: res.data.same_day_premium || 100 });
    } catch (err) {
      setSlots([]);
      toast.error("Couldn't load availability", { description: 'Please try another date or call us.' });
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleCalendarSelect = (dateObj) => {
    if (!dateObj) return;
    const iso = toISO(dateObj);
    setSelectedDate(iso);
    setPickerOpen(false);
    fetchSlots(iso);
  };

  const dayStatus = (dateObj) => monthDays[toISO(dateObj)]?.status;
  const isDayDisabled = (dateObj) => dayStatus(dateObj) !== 'open';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot) {
      toast.error('Please choose a date and time', { description: 'Select an available time slot to book.' });
      return;
    }
    setIsSubmitting(true);

    const payload = {
      name: formData.name,
      phone: formData.phone,
      address: formData.address,
      email: formData.email || undefined,
      notes: formData.notes || undefined,
      smsConsent: formData.smsConsent,
      lat: coords ? coords.lat : undefined,
      lng: coords ? coords.lng : undefined,
      date: selectedDate,
      time: selectedSlot.time,
      origin_url: window.location.origin,
    };

    try {
      const res = await axios.post(`${API}/api/appointments`, payload);
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'schedule_request_submitted', {
          zone,
          date: selectedDate,
          time: selectedSlot.time,
          sms_opt_in: formData.smsConsent,
          same_day: sameDayInfo.same_day,
        });
      }
      // Same-day bookings must pay the premium first -> redirect to Stripe Checkout
      if (res.data.status === 'payment_required' && res.data.checkout_url) {
        toast.loading('Redirecting to secure payment…', { id: 'pay' });
        window.location.href = res.data.checkout_url;
        return;
      }
      const appt = res.data.appointment || res.data;
      toast.success('Appointment Booked!', {
        description: `${selectedSlot.label} — arrival window ${appt.arrival_window}. Check your email/phone for confirmation.`,
      });
      setFormData({ name: '', phone: '', email: '', address: '', notes: '', smsConsent: false });
      setCoords(null);
      setSelectedDate('');
      setSelectedSlot(null);
      setSlots([]);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error('That slot was just taken', { description: 'Please pick another time.' });
        fetchSlots(selectedDate);
      } else if (status === 503) {
        toast.error('Online booking is being set up', { description: 'Please call or text us at 469-505-4321 to schedule.' });
      } else {
        toast.error('Something went wrong', { description: 'Please call or text us at 469-505-4321.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={`onepage-site site-theme ${SEASONS[getSeason()].theme}`} data-testid="backflow-page">
      <SiteNav />

      <section id="home" className="onepage-hero">
        <div className="onepage-hero-overlay"></div>
        <div className="onepage-hero-content">
          <h1 className="onepage-hero-title" data-testid="backflow-hero-title">
            Backflow Testing &amp; Repair
          </h1>

          <p className="onepage-hero-subtitle">
            Residential Irrigation Backflow Testing — $130
          </p>

          <div className="onepage-hero-subtitle-2">
            <p>Received a testing notice? Certified testing and repair for irrigation backflow assemblies.</p>
            <p>We complete the test and submit the results to your city.</p>
          </div>

          <div className="onepage-hero-actions">
            <Button size="lg" className="onepage-hero-btn" onClick={() => scrollToSection('contact')} data-testid="hero-schedule-button">
              Schedule Your Test
            </Button>

            <a href="tel:4695054321" className="onepage-phone-link" data-testid="hero-phone-link">
              Call/Text 469-505-4321
            </a>
          </div>
        </div>
      </section>

      <section id="waxahachie" className="onepage-why-section">
        <div className="onepage-container">
          <h2 className="onepage-section-title">
            Waxahachie Residential Backflow Testing
          </h2>

          <p className="onepage-section-text">
            PBNJ Backflow Testing makes the process simple. We perform the certified test,
            complete the required documentation, and submit the results to the city.
          </p>

          <div className="onepage-features">
            <div className="onepage-feature-item">
              <h4>$130 Residential Testing</h4>
              <p>Clear pricing for residential irrigation backflow tests.</p>
            </div>

            <div className="onepage-feature-item">
              <h4>City Reporting Included</h4>
              <p>We submit the completed test results for compliance.</p>
            </div>

            <div className="onepage-feature-item">
              <h4>Fast Scheduling</h4>
              <p>Most residential tests take less than 30 minutes.</p>
            </div>

            <div className="onepage-feature-item">
              <h4>Licensed & Insured</h4>
              <p>State certified backflow testing for residential irrigation systems.</p>
            </div>
          </div>

          <Button size="lg" className="onepage-hero-btn" onClick={() => scrollToSection('contact')} data-testid="waxahachie-schedule-button">
            Schedule Your Test
          </Button>
        </div>
      </section>

      <section id="services" className="onepage-services-section">
        <div className="onepage-container">
          <h2 className="onepage-services-title">
            Backflow Testing, Repairs & Compliance
          </h2>

          <p className="onepage-services-intro">
            We service residential irrigation and commercial backflow assemblies.
            We do not service fire system backflow devices.
          </p>

          <div className="onepage-services-grid">
            {services.map((service) => (
              <div key={service.id} className="onepage-service-card">
                <h3 className="onepage-service-name">{service.title}</h3>
                <p className="onepage-service-desc">{service.description}</p>
                <ul className="onepage-service-features">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <CheckCircle size={16} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="onepage-services-footer">
            From homeowners to commercial property managers, PBNJ Backflow Testing helps keep your property compliant.
          </p>
        </div>
      </section>

      <section className="onepage-cities-section">
        <div className="onepage-container">
          <h2 className="onepage-cities-title">Serving Waxahachie & the Greater DFW Area</h2>
        </div>
      </section>

      <section id="contact" className="onepage-contact-section">
        <div className="onepage-container">
          <div className="onepage-contact-grid">
            <div className="onepage-contact-form">
              <h2 className="onepage-contact-title">Book Your Backflow Test</h2>
              <p className="onepage-contact-subtitle">
                Pick a date and an available time — we’ll confirm by email and add it to our schedule.
              </p>

              <form onSubmit={handleSubmit} className="onepage-form">
                <div className="onepage-form-group">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" name="name" type="text" placeholder="Your name"
                    value={formData.name} onChange={handleChange} required />
                </div>

                <div className="onepage-form-group">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" name="phone" type="tel" placeholder="(469) 505-4321"
                    value={formData.phone} onChange={handleChange} required />
                </div>

                <div className="onepage-form-group">
                  <Label htmlFor="email">Email (optional)</Label>
                  <Input id="email" name="email" type="email" placeholder="your.email@example.com"
                    value={formData.email} onChange={handleChange} />
                </div>

                <div className="onepage-form-group">
                  <Label htmlFor="address">Service Address</Label>
                  <Input id="address" name="address" type="text" placeholder="Start typing your address..."
                    ref={addressInputRef} autoComplete="off"
                    value={formData.address} onChange={handleChange} required
                    data-testid="address-input" />
                </div>

                <div className="onepage-form-group">
                  <Label>Choose a Date</Label>
                  <Popover open={pickerOpen} onOpenChange={(o) => inServiceArea && setPickerOpen(o)}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="onepage-date-trigger"
                        data-testid="booking-date-trigger"
                        disabled={!inServiceArea}
                      >
                        <CalendarDays size={18} />
                        <span>{selectedDate ? prettyDate(selectedDate) : 'Select an available date'}</span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="onepage-calendar-popover" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate ? new Date(`${selectedDate}T00:00:00`) : undefined}
                        onSelect={handleCalendarSelect}
                        month={calendarMonth}
                        onMonthChange={setCalendarMonth}
                        fromDate={todayDate()}
                        toDate={maxDate()}
                        disabled={isDayDisabled}
                        modifiers={{
                          full: (d) => dayStatus(d) === 'full',
                          closed: (d) => dayStatus(d) === 'closed',
                        }}
                        modifiersClassNames={{
                          full: 'rdp-day_full',
                          closed: 'rdp-day_closed',
                        }}
                      />
                      <div className="onepage-calendar-legend">
                        <span><i className="legend-dot open" /> Open</span>
                        <span><i className="legend-dot full" /> Full</span>
                        <span><i className="legend-dot closed" /> Closed</span>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {!coords ? (
                    <span className="onepage-field-hint" data-testid="address-gate-hint">
                      Start typing your address and <strong>select it from the suggestions</strong> — available dates depend on your location.
                    </span>
                  ) : !inServiceArea ? (
                    <span className="onepage-field-hint onepage-field-hint-error" data-testid="out-of-area-hint">
                      Sorry, that address is outside our service area. Please call or text us at{' '}
                      <a href="tel:4695054321">469-505-4321</a> to check availability.
                    </span>
                  ) : (
                    <span className="onepage-field-hint">
                      {zone === 'waxahachie'
                        ? 'Waxahachie: we book Thu–Sat first; earlier weekdays open as those fill.'
                        : 'Outside Waxahachie: appointments are Mon–Wed, 11:30 AM–7:00 PM.'}
                    </span>
                  )}
                </div>

                {selectedDate && (
                  <div className="onepage-form-group">
                    <Label>Choose a Time</Label>
                    {loadingSlots ? (
                      <p className="onepage-slots-status">Loading available times…</p>
                    ) : slots.length === 0 ? (
                      <p className="onepage-slots-status">No available times for this date. Please pick another day or call us.</p>
                    ) : (
                      <div className="onepage-slots-grid" data-testid="slots-grid">
                        {slots.map((s) => (
                          <button
                            key={s.time}
                            type="button"
                            className={`onepage-slot-btn${selectedSlot && selectedSlot.time === s.time ? ' selected' : ''}`}
                            onClick={() => setSelectedSlot(s)}
                            data-testid={`slot-${s.time}`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {selectedSlot && (
                  <div className="onepage-window-note" data-testid="arrival-window-note">
                    <strong>Arrival window:</strong> {selectedSlot.arrival_window}.<br />
                    You do not need to be present if your backflow device is accessible outside your fence (most are).
                    If it’s behind a locked gate, leave it unlocked or add a gate code in the notes.
                  </div>
                )}

                <div className="onepage-form-group">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea id="notes" name="notes" placeholder="Gate code, best time to reach you, or any questions..."
                    rows={3} value={formData.notes} onChange={handleChange} />
                </div>

                <label className="onepage-consent-optin" data-testid="sms-consent-optin">
                  <input
                    type="checkbox"
                    name="smsConsent"
                    checked={formData.smsConsent}
                    onChange={(e) => setFormData((prev) => ({ ...prev, smsConsent: e.target.checked }))}
                    data-testid="sms-consent-checkbox"
                  />
                  <span>
                    <strong>(Optional)</strong> I agree to receive SMS text messages from PBNJ Backflow
                    Testing about my appointment (confirmations, scheduling, and reminders). Message &amp;
                    data rates may apply; message frequency varies. Reply STOP to opt out, HELP for help.
                    Consent is not a condition of booking. We send only transactional appointment messages —
                    we will <strong>never</strong> use your number for advertising, marketing, or promotional texts.
                  </span>
                </label>

                {selectedSlot && sameDayInfo.same_day && (
                  <div className="onepage-premium-note" data-testid="same-day-premium-note">
                    <strong>⚡ Same-day service — ${sameDayInfo.premium} premium</strong>
                    <span>
                      This is a same-day appointment. A ${sameDayInfo.premium} same-day service charge applies and is
                      <strong> paid upfront online</strong>. After you click below you'll be taken to our secure
                      payment page; your appointment is confirmed once payment is received.
                    </span>
                  </div>
                )}

                <Button type="submit" size="lg" className="onepage-form-submit" disabled={isSubmitting} data-testid="contact-form-submit">
                  {isSubmitting
                    ? 'Booking...'
                    : (selectedSlot && sameDayInfo.same_day
                        ? `Pay $${sameDayInfo.premium} & Confirm Same-Day`
                        : 'Confirm Appointment')}
                </Button>

                <p className="onepage-consent-note" data-testid="sms-consent-note">
                  Checking the box above is optional and not required to book. We text only customers who opt
                  in, and only about their appointment — no marketing. Your number is never shared or sold.
                  See our <Link to="/privacy">Privacy Policy</Link> and <Link to="/terms">Terms</Link>.
                </p>
              </form>
            </div>

            <div className="onepage-contact-info">
              <h3 className="onepage-contact-info-title">Contact PBNJ</h3>

              <div className="onepage-contact-details">
                <div className="onepage-contact-item">
                  <Phone size={20} />
                  <div>
                    <div className="onepage-contact-label">Call or Text:</div>
                    <a href="tel:4695054321" className="onepage-contact-value">469-505-4321</a>
                  </div>
                </div>

                <div className="onepage-contact-item">
                  <Mail size={20} />
                  <div>
                    <div className="onepage-contact-label">Email:</div>
                    <a href="mailto:info@pbnjbackflowtesting.com" className="onepage-contact-value">
                      info@pbnjbackflowtesting.com
                    </a>
                  </div>
                </div>

                <div className="onepage-contact-item">
                  <Clock size={20} />
                  <div>
                    <div className="onepage-contact-label">Hours:</div>
                    <div className="onepage-contact-value">Mon–Sat, 9:00 AM–7:00 PM</div>
                  </div>
                </div>

                <div className="onepage-contact-item">
                  <MapPin size={20} />
                  <div>
                    <div className="onepage-contact-label">Service Area:</div>
                    <div className="onepage-contact-value">Waxahachie & Greater DFW</div>
                  </div>
                </div>
              </div>

              <div className="onepage-referral-box">
                <h4>$25 Referral Bonus</h4>
                <p>
                  Refer a friend, family member, or neighbor. If they receive a backflow test
                  and mention your name and address number, we’ll send you a $25 Amazon Gift Card.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <a href="tel:4695054321" className="pbnj-sticky-call" data-testid="sticky-call-button">
        Call/Text 469-505-4321
      </a>

      <SiteFooter />
    </div>
  );
};

export default OnePage;
